import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/* ═══════════════════════════════════════════
   EDITABLE: Hero slides data
   - Edit headline, subtext, buttonText, buttonLink, bgImage for each slide
═══════════════════════════════════════════ */
const slides = [
  {
    id: 1,
    headline: 'Peaceful Present, Secured & Happy Future.',
    subtext: 'Plan Better. Spend Smarter. Stay Prepared with the Ashwamedh Team.',
    buttonText: 'Explore Services',
    buttonLink: '#services',
    bgImage: 'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=1920&q=80',
  },
  {
    id: 2,
    headline: 'Small Steps Today, Strong Future Tomorrow.',
    subtext: 'Learn how simple saving and smart investments go a long way — with Ashwamedh.',
    buttonText: 'Know More',
    buttonLink: '#about',
    bgImage: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80',
  },
];

export const HeroSlider = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (index) => setCurrent(index);
  const prev = () => setCurrent((current - 1 + slides.length) % slides.length);
  const next = () => setCurrent((current + 1) % slides.length);

  return (
    <section id="home" data-testid="hero-slider-section" className="relative h-[90vh] md:h-screen overflow-hidden pt-20 md:pt-24">
      {/* Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={slides[current].id}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="absolute inset-0"
        >
          {/* Background */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${slides[current].bgImage})` }}
          />
          <div className="absolute inset-0 bg-[#0A192F]/70" />

          {/* Content */}
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="max-w-2xl"
              >
                <h1
                  data-testid={`hero-headline-${current}`}
                  className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
                >
                  {slides[current].headline}
                </h1>
                <p className="text-base sm:text-lg text-white/80 mb-8 leading-relaxed font-['Outfit']">
                  {slides[current].subtext}
                </p>
                <a
                  href={slides[current].buttonLink}
                  data-testid={`hero-cta-${current}`}
                  className="inline-block px-8 py-3.5 bg-[#D4AF37] text-[#0A192F] font-semibold rounded-full hover:bg-[#C5A059] hover:scale-105 transition-all duration-300 shadow-lg"
                >
                  {slides[current].buttonText}
                </a>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      <button
        data-testid="hero-prev-btn"
        onClick={prev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-[#D4AF37] hover:text-[#0A192F] transition-all"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        data-testid="hero-next-btn"
        onClick={next}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-[#D4AF37] hover:text-[#0A192F] transition-all"
      >
        <ChevronRight size={24} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            data-testid={`hero-dot-${index}`}
            onClick={() => goTo(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === current ? 'bg-[#D4AF37] scale-125' : 'bg-white/40 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </section>
  );
};
